
import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDocumentManagement } from '@/hooks/admin/identity-docs/useDocumentManagement';
import type { DocumentProps } from '@/hooks/admin/identity-docs/useSubmittedDocuments';

interface DocumentDeleteDialogProps {
  document: DocumentProps | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDocumentDeleted?: () => void;
}

export function DocumentDeleteDialog({
  document,
  open,
  onOpenChange,
  onDocumentDeleted
}: DocumentDeleteDialogProps) {
  const { deleteDocument, isProcessing } = useDocumentManagement();
  const [error, setError] = useState<string | null>(null);
  
  const handleDelete = async () => {
    if (!document) return;
    
    setError(null);
    const success = await deleteDocument(document.id);
    
    if (success) {
      onOpenChange(false);
      if (onDocumentDeleted) {
        onDocumentDeleted();
      }
    } else {
      setError("Failed to delete document. It may have verification history records associated with it.");
    }
  };

  const isPrimary = document?.primary_document;
  
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isPrimary ? "Delete Primary Document?" : "Delete Document?"}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            {isPrimary && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-3 rounded-md mb-3">
                <strong>Warning:</strong> This is a primary verification document. 
                Deleting it will revert the user's verification status.
              </div>
            )}
            
            <div>
              This action cannot be undone. The document will be permanently 
              removed from both the database and storage.
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-md my-3">
                <strong>Error:</strong> {error}
              </div>
            )}
            
            {document && (
              <div className="mt-2 bg-gray-50 p-3 rounded-md">
                <p><strong>Document type:</strong> {document.id_document_types?.document_name}</p>
                {document.document_number && (
                  <p><strong>Document number:</strong> {document.document_number}</p>
                )}
                <p><strong>User:</strong> {document.profiles?.first_name} {document.profiles?.last_name}</p>
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isProcessing}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleDelete();
            }}
            disabled={isProcessing}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {isProcessing ? "Deleting..." : "Delete Document"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
